import React, { useMemo } from 'react'
import type { QuoteFile } from 'paintscout'
import { getImageWidth, getColumnWidth, gridPrintStyles } from './filesGridLayout'
import {
  createStyles,
  withStyles,
  Tooltip,
  makeStyles,
  Button,
  FilePreview,
  Grid,
  Dropzone,
  Spinner,
  useTranslation
} from '@paintscout/ui'
import type { WithStyles, MuiTheme as Theme, DropzoneProps } from '@paintscout/ui'
import PictureAsPdfOutlinedIcon from '@material-ui/icons/PictureAsPdfOutlined'
import { useUser } from '@paintscout/ui-business'

import { SortableContainer, SortableElement, arrayMove } from 'react-sortable-hoc'
import type { SortableContainerProps, SortableElementProps } from 'react-sortable-hoc'
import { useConnection } from 'react-detect-offline'
import { TRIAL_QUOTE_FILES_LIMIT } from '@paintscout/util'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'

import { getFiles } from '@paintscout/util/builder'
import classnames from 'classnames'
import { useQuote } from '../context'

export interface FilesGridProps {
  files: QuoteFile[]
  editable?: boolean
  showUploadDropzone?: boolean
  responsiveTransformation?: boolean
  columns?: number
  showBorder?: boolean
  justify?: 'space-around' | 'space-between' | 'center' | 'flex-start' | 'flex-end'
  isLoading?: boolean
  /**
   * Reorder auto-scroll only works if react-sortable-hoc knows the real scroll container.
   * On the full-page estimate/invoice the page scrolls at the window level (no `overflow:auto`
   * ancestor), so autoscroll can't reach off-screen drop targets on touch. Set this true in that
   * context to use the window as the scroll container. Leave false inside dialogs, where the
   * `DialogContent` (overflow-y:auto) is found automatically (#21264).
   */
  useWindowAsScrollContainer?: boolean

  onReorder?: (files: QuoteFile[]) => any
  onUpload?: (files: File[]) => any
  onFileClick?: (index: number) => any
  onLoadMore?: () => any
}

type FilesGridPropsWithInjections = FilesGridProps & WithStyles<typeof styles>

const styles = createStyles({
  dragging: {
    zIndex: 2_147_483_002
  },
  ...gridPrintStyles
})

/**
 * Print: let a single-line, ellipsised label render in full. Paper has no hover and no tooltip, so
 * anything trimmed on screen is simply lost there (#22505).
 */
const printFullSingleLine = {
  whiteSpace: 'normal',
  overflow: 'visible',
  textOverflow: 'clip',
  wordBreak: 'break-word'
} as const

/** Print: same idea for the `-webkit-box` line-clamped captions - drop the clamp entirely. */
const printFullClampedText = {
  display: 'block',
  WebkitLineClamp: 'unset',
  overflow: 'visible',
  wordBreak: 'break-word'
} as const

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  uploadButton: {},
  ...gridPrintStyles,
  tile: ({ showBorder }: { showBorder: boolean }) => ({
    position: 'relative',
    width: '100%',
    aspectRatio: '4/3',
    borderRadius: theme.borderRadius.md,
    overflow: 'hidden',
    border: showBorder === false ? 'none' : `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    background: theme.palette.common.white,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    // Suppress native image drag / text selection / iOS callout so the press gesture
    // reaches react-sortable-hoc instead of starting a native image drag (#20637).
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
    '& img': {
      WebkitUserDrag: 'none',
      userSelect: 'none'
    },
    '&:hover': {
      borderColor: theme.paintscout.palette.brand.Neutral.P70
    },
    '@media print': {
      pageBreakInside: 'avoid',
      // In print the caption flows below the image instead of overlaying it, so the tile can no
      // longer be a fixed-ratio box that clips its contents - it grows to fit the caption (#22505).
      aspectRatio: 'auto',
      overflow: 'visible'
    }
  }),
  tilePdf: {
    background: theme.paintscout.palette.brand.Neutral.P96
  },
  tileSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
  },
  filePreviewRoot: {
    padding: 0,
    width: '100%',
    height: '100%',
    minHeight: 0,
    minWidth: 0
  },
  filePreviewImageCover: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    // A printed photo is a reference document, not a thumbnail - crop nothing (#22505).
    '@media print': {
      objectFit: 'contain'
    }
  },
  imageMediaSlot: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    overflow: 'hidden',
    display: 'flex',
    '@media print': {
      // The tile no longer sets the ratio in print (it grows to fit the caption), so the image
      // slot holds the ratio instead.
      flex: '0 0 auto',
      aspectRatio: '4/3'
    }
  },
  imageOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: theme.spacing(1.5),
    background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.35) 60%, rgba(0,0,0,0) 100%)',
    color: theme.palette.common.white,
    pointerEvents: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    // Print/PDF: leave the absolute overlay and sit below the image, like the pre-#21305 grid did.
    // Field crews read these captions off paper, and the overlay clipped both the caption text and
    // the photo under it (#22505).
    '@media print': {
      position: 'static',
      background: 'none',
      color: 'inherit',
      padding: theme.spacing(0.5, 0, 0, 0)
    }
  },
  imageTitle: {
    ...theme.typography.subtitle2,
    color: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightMedium,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '@media print': {
      color: theme.paintscout.palette.brand.Neutral.P30,
      ...printFullSingleLine
    }
  },
  imageCaption: {
    ...theme.typography.caption,
    color: theme.palette.common.white,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    // No line clamp in print: a truncated caption is the whole complaint in #22505.
    '@media print': {
      color: theme.paintscout.palette.brand.Neutral.P50,
      ...printFullClampedText
    }
  },
  pdfPreviewSlot: {
    flex: '1 1 auto',
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    padding: theme.spacing(1.5, 1.5, 0.5, 1.5),
    '& canvas, & img': {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain'
    }
  },
  pdfMeta: {
    flex: '0 0 auto',
    padding: theme.spacing(0.5, 1.5, 1, 1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25)
  },
  pdfTitle: {
    ...theme.typography.caption,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.paintscout.palette.brand.Neutral.P30,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '@media print': printFullSingleLine
  },
  pdfCaption: {
    ...theme.typography.caption,
    color: theme.paintscout.palette.brand.Neutral.P50,
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    '@media print': printFullClampedText
  },
  pdfBadge: {
    ...theme.typography.caption,
    color: theme.paintscout.palette.brand.Neutral.P50,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  selected: {
    boxShadow: `0 0 0 2px ${theme.palette.primary.main} inset`
  },
  check: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    zIndex: 10,
    color: '#fff',
    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))'
  },
  item: {
    '@media print': {
      pageBreakInside: 'avoid'
    }
  }
}))

function isPdfFile(file?: QuoteFile): boolean {
  if (!file) return false
  if (file.format === 'pdf' || file.type === 'application/pdf') return true
  if (!file.src) return false
  return file.src.split('?')[0]?.toLowerCase().endsWith('.pdf') ?? false
}

interface SortableFilePreviewProps extends SortableElementProps {
  file?: QuoteFile
  columns?: number
  showBorder: boolean
  classes?: any
  editable?: boolean
  onClick?: () => any
}

const SortableFilePreview: React.ComponentClass<SortableFilePreviewProps, any> = SortableElement(
  ({ file, columns, onClick, ...props }: SortableFilePreviewProps) => {
    const classes = useStyles(props)
    const { __ } = useTranslation()
    let transformationProps: any = {
      ...getImageWidth(columns),
      crop: 'pad',
      background: '#fff'
    }

    if ((file.type ?? 'image').startsWith('video')) {
      transformationProps = {
        ...transformationProps,
        controls: false
      }
    }

    const { columnWidth, columnClass } = getColumnWidth(columns, classes)
    const isPdf = isPdfFile(file)
    const hasOverlayText = !isPdf && !!(file?.title || file?.caption)

    return (
      <Grid
        item
        key={file.key}
        xs={12}
        sm={columnWidth}
        md={columnWidth}
        className={classnames(columnClass, classes.item)}
      >
        <div className={classnames(classes.tile, isPdf && classes.tilePdf, file.selected && classes.tileSelected)}>
          {file.selected && <CheckCircleIcon className={classes.check} />}
          {isPdf ? (
            <>
              <div className={classes.pdfPreviewSlot}>
                <FilePreview
                  classes={{ root: classes.filePreviewRoot }}
                  file={file}
                  videoThumbnail={true}
                  onClick={onClick}
                  {...transformationProps}
                />
              </div>
              <div className={classes.pdfMeta}>
                {file?.title && (
                  <div className={classes.pdfTitle} title={file.title}>
                    {file.title}
                  </div>
                )}
                {file?.caption && (
                  <div className={classes.pdfCaption} title={file.caption}>
                    {file.caption}
                  </div>
                )}
                <div className={classes.pdfBadge}>
                  <PictureAsPdfOutlinedIcon fontSize="small" />
                  {__('PDF')}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={classes.imageMediaSlot}>
                <FilePreview
                  classes={{
                    root: classes.filePreviewRoot,
                    fileRoot: classes.filePreviewImageCover,
                    image: classes.filePreviewImageCover
                  }}
                  file={file}
                  videoThumbnail={true}
                  onClick={onClick}
                  {...transformationProps}
                />
              </div>
              {/*
                Sibling of the media slot, not a child of it: on screen the overlay is absolutely
                positioned against `.tile`, so its DOM position changes nothing visually - but in
                print it becomes `position: static` and must come *after* the image in the tile's
                column flow to sit below it (#22505).
              */}
              {hasOverlayText && (
                <div className={classes.imageOverlay}>
                  {file?.title && (
                    <div className={classes.imageTitle} title={file.title}>
                      {file.title}
                    </div>
                  )}
                  {file?.caption && (
                    <div className={classes.imageCaption} title={file.caption}>
                      {file.caption}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </Grid>
    )
  }
)

interface SortableGridContainerProps extends SortableContainerProps {
  files: QuoteFile[]
  disabled: boolean
  showUploadDropzone?: boolean
  columns: number
  editable: boolean
  showBorder: boolean
  justify: 'space-around' | 'space-between' | 'center' | 'flex-start' | 'flex-end'
  classes?: any
  onUpload?: (files: File[]) => any
  onClick?: (index: number) => any
  onLoadMore?: () => Promise<boolean>
}

const SortableGridContainer: React.ComponentClass<SortableGridContainerProps, any> = SortableContainer(
  ({
    files,
    disabled,
    showUploadDropzone,
    columns,
    showBorder,
    editable,
    justify,
    onUpload,
    onClick,
    onLoadMore,
    classes
  }: SortableGridContainerProps) => {
    const { online } = useConnection()
    const { quote } = useQuote()
    const { isTrial } = useUser()
    const { __ } = useTranslation()
    const [loadingMore, setLoadingMore] = React.useState(false)
    // calculate initial number of quote files so we know how many files are left without needing
    // to update the quote first
    const numQuoteFiles = useMemo(() => getFiles({ quote, attached: true }).length - files.length, [quote])

    const canAddFiles = !isTrial || numQuoteFiles + files.length < TRIAL_QUOTE_FILES_LIMIT

    return (
      <Grid container spacing={1} alignItems={'stretch'} justifyContent={justify}>
        {files.map((file, index) => (
          <SortableFilePreview
            key={file.key}
            index={index}
            file={file}
            editable={editable}
            showBorder={showBorder}
            columns={columns}
            disabled={disabled}
            onClick={() => {
              if (onClick) {
                onClick(index)
              }
            }}
          />
        ))}
        {onLoadMore && (
          <Grid item key={'loadMore'} xs={12} sm={12} md={12} className={classes.grid6}>
            <Button
              // Clicking through here must not fall into EditableQuote's onClickCapture, which
              // treats any click on a non-editable quote as "enter edit mode" and swallows the
              // event before it reaches this button (#14984 QA feedback).
              id="allow-propagation"
              onClick={async () => {
                setLoadingMore(true)
                await onLoadMore()
                setLoadingMore(false)
              }}
              variant="outlined"
              fullWidth
              loading={loadingMore}
              disabled={!online}
            >
              {__('Load More')}
            </Button>
          </Grid>
        )}
        {showUploadDropzone && (
          <Grid item key={'upload'} xs={12} sm={12} md={12} className={classes.grid6}>
            <Tooltip title={!canAddFiles ? __('You must upgrade your plan to add more media') : ''}>
              <div>
                <UploadDropzone onUpload={onUpload} disabled={!canAddFiles || !online || !editable} />
              </div>
            </Tooltip>
          </Grid>
        )}
      </Grid>
    )
  }
)

function UploadDropzone(props: { onUpload?: (files: File[]) => any } & Partial<DropzoneProps>) {
  const { __ } = useTranslation()

  return (
    <Dropzone
      text={__('Add Media')}
      onDropAccepted={props.onUpload}
      style={{ height: 'initial', marginTop: 20 }}
      {...props}
    />
  )
}

function FilesGridNext(props: FilesGridPropsWithInjections) {
  const {
    files,
    editable,
    showUploadDropzone,
    onUpload,
    columns = 4,
    showBorder = true,
    justify = 'flex-start',
    classes,
    onReorder,
    onFileClick,
    isLoading,
    onLoadMore,
    useWindowAsScrollContainer = false
  } = props

  function handleSortEnd({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) {
    const reorderedFiles = arrayMove(files, oldIndex, newIndex)

    if (onReorder) {
      onReorder(reorderedFiles)
    }
  }

  if (isLoading) {
    return <Spinner padded />
  }

  return (
    <>
      <SortableGridContainer
        classes={classes}
        helperClass={classes.dragging}
        pressDelay={150}
        useWindowAsScrollContainer={useWindowAsScrollContainer}
        files={files}
        editable={editable}
        axis="xy"
        columns={columns}
        showBorder={showBorder}
        justify={justify}
        onSortEnd={handleSortEnd}
        disabled={!editable}
        onClick={onFileClick}
        showUploadDropzone={showUploadDropzone}
        onUpload={onUpload}
        onLoadMore={onLoadMore}
      />
    </>
  )
}

export default withStyles(styles)(FilesGridNext)
